import { contrastOptions } from '../constants/UIConstants';

const mainColor = {
    [contrastOptions.YELLOW_ON_BLACK]: '#ffff00',
    [contrastOptions.YELLOW_ON_BLUE]: '#ffff00',
    [contrastOptions.BLACK_ON_CREAM]: '#000000',
    [contrastOptions.BLACK_ON_LIGHT_BLUE]: '#000000',
};

const bgColor = {
    [contrastOptions.YELLOW_ON_BLACK]: '#000000',
    [contrastOptions.YELLOW_ON_BLUE]: '#112E51',
    [contrastOptions.BLACK_ON_CREAM]: '#FFFEE7',
    [contrastOptions.BLACK_ON_LIGHT_BLUE]: '#F1F7FF',
};

let theme;

const themeColorCache = {};
let colorCache;
let mainColorRgb;
let bgColorRgb;
let colorDelta;
let colorShiftDirection;

const hexToRgb = (code) => {
    const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
    const hex = code.replace(shorthandRegex, (m, r, g, b) => {
        return r + r + g + g + b + b;
    });

    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
    } : null;
};

const rgbToHex = ({ r, g, b }) => {
    // eslint-disable-next-line no-bitwise
    return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
};

const adaptColor = (hex) => {
    if (colorCache[hex]) {
        return colorCache[hex];
    }
    if (isColorWhite(hex)) {
        colorCache[hex] = bgColor[theme];
        return bgColor[theme];
    }

    const rgb = hexToRgb(hex);
    if (!rgb) {
        colorCache[hex] = null;
        return hex;
    }

    const adaptedRgb = adaptRgbToTheme(rgb);
    colorCache[hex] = rgbToHex(adaptedRgb);
    return colorCache[hex];
};

const adaptRgbToTheme = (rgb) => {
    const shift = calcColorShift(rgb);
    return {
        r: adaptedColorLayer(shift, 'r'),
        g: adaptedColorLayer(shift, 'g'),
        b: adaptedColorLayer(shift, 'b'),
    };
};

const adaptedColorLayer = (shift, layer) => {
    const shiftSize = (colorDelta[layer] * shift[layer]) / 100;
    const adaptedLayer = mainColorRgb[layer] + (shiftSize * colorShiftDirection[layer]);
    return Math.round(adaptedLayer);
};

const calcColorShift = (rgb) => {
    return {
        r: (rgb.r * 100) / 255,
        g: (rgb.g * 100) / 255,
        b: (rgb.b * 100) / 255,
    };
};

const adaptColorAttribute = (el, attr) => {
    const adaptedColor = adaptColor(el.getAttribute(attr));

    if (isHex(adaptedColor)) {
        el.setAttribute(attr, adaptedColor);
    }
};

const isColorWhite = (color) => {
    return color && ['#ffffff', '#fff', 'white'].indexOf(color.toLocaleLowerCase()) !== -1;
};

const isHex = (hex) => {
    const reg = /^#[0-9a-f]{3,6}$/i;
    return hex && reg.test(hex);
};

const calcThemeColorDelta = () => {
    mainColorRgb = hexToRgb(mainColor[theme]);
    bgColorRgb = hexToRgb(bgColor[theme]);

    colorDelta = {
        r: Math.abs(bgColorRgb.r - mainColorRgb.r),
        g: Math.abs(bgColorRgb.g - mainColorRgb.g),
        b: Math.abs(bgColorRgb.b - mainColorRgb.b),
    };

    colorShiftDirection = {
        r: bgColorRgb.r > mainColorRgb.r ? 1 : -1,
        g: bgColorRgb.g > mainColorRgb.g ? 1 : -1,
        b: bgColorRgb.b > mainColorRgb.b ? 1 : -1,
    };
};

const adaptElementStyle = (node) => {
    const style = node.getAttribute('style');
    if (!style) {
        return;
    }

    let adaptationNeeded = false;
    const adaptableProps = ['fill', 'stroke', 'stop-color'];
    const updatedProps = [];

    style.split(/\s*;\s*/).forEach((property) => {
        const [key, value] = property.split(/\s*:\s*/);
        if (adaptableProps.includes(key)) {
            updatedProps.push(`${key}:${adaptColor(value)}`);
            adaptationNeeded = true;
        } else {
            updatedProps.push(property);
        }
    });

    if (adaptationNeeded) {
        node.setAttribute('style', updatedProps.join(';'));
    }
};

const setBaseFillColor = (svgNode) => {
    svgNode.setAttribute('fill', mainColor[theme]);
};

const adaptChildren = (parent) => {
    Array.from(parent.childNodes).forEach((node) => {
        if (node.nodeType !== 1) {
            return;
        }
        adaptColorAttribute(node, 'stroke');
        adaptColorAttribute(node, 'stop-color');
        adaptColorAttribute(node, 'fill');
        adaptElementStyle(node);

        adaptChildren(node);
    });
};

const svgImageAdapterService = {
    setTheme(svgNode, themeName) {
        if (!themeColorCache[themeName]) {
            themeColorCache[themeName] = {};
        }

        colorCache = themeColorCache[themeName];
        theme = themeName;

        if (mainColor[theme] && bgColor[theme]) {
            calcThemeColorDelta();
            setBaseFillColor(svgNode);
            adaptChildren(svgNode);
        }
    },
};

export default svgImageAdapterService;
